import { EditOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  SensorDescriptor,
  SensorOptions,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { forwardRef, useMemo } from 'react';

import { Button, LoadingCard, Text } from 'src/client/components';
import GiveCardV2 from 'src/client/pages/DonorGalleryV2/components/CardsSection/components/GiveCard';
import { PageRecipient } from 'src/commons/types';

import GiveCardBase from '../GiveCard';

import * as stylesheet from './styles';

type Props = {
  featuredPageRecipients: PageRecipient[] | undefined;
  isLoading: boolean;
  isTogetherViewActive?: boolean;
  onManageTogetherView?: () => void;
  onHidePageRecipient: (pageRecipientId: string) => void;
  onUnfeaturePageRecipient: (pageRecipientId: string) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragStart: (event: DragEndEvent) => void;
  sensors: SensorDescriptor<SensorOptions>[];
  styles?: any;
};

const FeaturedCardsEditMode = forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const {
      featuredPageRecipients,
      isLoading,
      isTogetherViewActive = false,
      onManageTogetherView,
      onHidePageRecipient,
      onUnfeaturePageRecipient,
      sensors,
      styles,
      handleDragEnd,
      handleDragStart,
    } = props;

    const S = styles ?? stylesheet;
    const GiveCard = styles ? GiveCardV2 : GiveCardBase;

    const screens = useBreakpoint();

    const featuredPageRecipientIds = useMemo(
      () => featuredPageRecipients?.map((pageRecipient) => pageRecipient.id),
      [featuredPageRecipients]
    );

    const featuredCards = useMemo(
      () =>
        featuredPageRecipients?.map((pageRecipient) => (
          <GiveCard
            isEditMode
            data-page-recipient-visibility="featured-card"
            editModeConfig={{
              primaryButtonText: 'Unfeature',
              primaryButtonProps: {
                onClick: () => onUnfeaturePageRecipient(pageRecipient.id),
              },
              secondaryButtonProps: {
                onClick: () => onHidePageRecipient(pageRecipient.id),
              },
            }}
            isTogetherViewActive={isTogetherViewActive}
            key={pageRecipient.id}
            pageRecipient={pageRecipient}
          />
        )),
      [
        featuredPageRecipients,
        GiveCard,
        isTogetherViewActive,
        onUnfeaturePageRecipient,
        onHidePageRecipient,
      ]
    );

    const instructionText = useMemo(
      () => (
        <div>
          To feature a recipient, {screens.lg ? 'hover' : 'tap'} over them and
          click <span>Feature</span>
        </div>
      ),
      [screens.lg]
    );

    const spotlightText = useMemo(
      () => (
        <S.SpotlightSection>
          <Text type="h5med2">Spotlight up to 3 causes you care about</Text>
          <S.SpotlightText type="body2reg2">
            You can feature up to 3 recipients in your gallery. Select a
            recipient to feature. Drag and drop to reorder.
          </S.SpotlightText>
        </S.SpotlightSection>
      ),
      [S]
    );

    const togetherViewSection = useMemo(
      () => (
        <S.TogetherViewSection>
          <Text type="h5med2">Together View</Text>
          <S.SpotlightText type="body2reg2">
            This view lets you display a small curated set of up to six
            recipients from your Giving Side. Drag to reorder as desired. This
            does not affect the order or content of your full gallery.
          </S.SpotlightText>
          <Button type="primary" onClick={onManageTogetherView}>
            <EditOutlined />
            Manage your Together View
          </Button>
        </S.TogetherViewSection>
      ),
      [S, onManageTogetherView]
    );

    const renderTextSection = useMemo(
      () => (isTogetherViewActive ? togetherViewSection : spotlightText),
      [isTogetherViewActive, togetherViewSection, spotlightText]
    );

    return (
      <DndContext
        autoScroll={{
          acceleration: 500,
        }}
        modifiers={[restrictToParentElement]}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <S.StyledDragAndDropArea
          data-cy="featured-cards-drag-and-drop-area"
          emptyStateInstructionText={
            isTogetherViewActive ? togetherViewSection : instructionText
          }
          instructionTabText="Drag and drop recipients to rearrange their order"
          isEmpty={!featuredPageRecipients?.length}
          ref={ref}
        >
          {renderTextSection}
          <LoadingCard
            isLoading={isLoading}
            spinProps={{
              'data-cy': 'featured-cards-spinner',
            }}
          >
            <SortableContext items={featuredPageRecipientIds as string[]}>
              <S.FeaturedCardsContainer>
                {featuredCards}
              </S.FeaturedCardsContainer>
            </SortableContext>
          </LoadingCard>
        </S.StyledDragAndDropArea>
      </DndContext>
    );
  }
);

export default FeaturedCardsEditMode;
